import { useState, useRef } from 'react'

import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'

import {
  Box, Stack, StackProps,
  List, ListItem, ListItemIcon, ListItemText, ListItemButton,
  Button, Checkbox
} from "@mui/material"

import { PrefectureResult, useListPrefecture } from 'interface/prefecture'

type PrefectureListProps = {
  selectedBlockCode: string
  defaultPrefectureCodes: string[]
  onSelectedPrefectures: (prefectures: PrefectureResult[]) => void
} & StackProps
export const PrefectureList = ({
  selectedBlockCode,
  defaultPrefectureCodes,
  onSelectedPrefectures,
  ...others
}: PrefectureListProps) => {
  const inputTimer = useRef<NodeJS.Timeout | null>(null)
  const [selectedPrefectureCodes, setSelectedPrefectureCodes] = useState<string[]>(defaultPrefectureCodes)

  const prefectureState = useListPrefecture(selectedBlockCode)
  const allSelected = prefectureState.data ? !prefectureState.data.list.some((prefecture) => selectedPrefectureCodes.indexOf(prefecture.prefectureCode) < 0) : false

  const onClickAll = () => {
    if (!prefectureState.data) return
    applySelectedPrefectures(
      allSelected ? [] : prefectureState.data.list.map((prefecture) => prefecture.prefectureCode)
    )
  }
  const onClickPrefecture = (value: string) => () => {
    const currentIndex = selectedPrefectureCodes.indexOf(value)
    const newChecked = [...selectedPrefectureCodes]
    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }
    setSelectedPrefectureCodes(newChecked)

    if (inputTimer.current) clearTimeout(inputTimer.current)
    const newTimer = setTimeout(() => {
      applySelectedPrefectures(newChecked)
      inputTimer.current = null
    }, 500)
    inputTimer.current = newTimer
  }

  const findPrefectures = (codes: string[] | null) => {
    return ((!prefectureState.data || !codes || codes.length === 0) ? [] : codes)
      .map((prefectureCode) => (prefectureState.data?.list || []).find((prefecture) => prefecture.prefectureCode === prefectureCode))
      .filter((prefecture): prefecture is NonNullable<PrefectureResult> => prefecture !== undefined)
  }
  const applySelectedPrefectures = (codes: string[]) => {
    setSelectedPrefectureCodes(codes)
    onSelectedPrefectures(findPrefectures(codes))
  }

  return (
    <Stack spacing={1} direction='column' {...others}>
      <Box paddingX={1}>
        <Button
          fullWidth={true}
          variant="outlined"
          onClick={onClickAll}
          size="small"
        >{ allSelected ? '選択解除' : '全選択'}</Button>
      </Box>
      <List sx={{
        flexGrow: 1,
        overflow: 'auto'
      }} disablePadding>
        { prefectureState.loading && <Loading /> }

        <ErrorBlock error={prefectureState.error} paddingBottom={1} paddingX={1}/>

        { (prefectureState.data?.list || []).map((prefecture) => {
          const labelId = `list-label-${prefecture.prefectureCode}`
          return (
            <ListItem
              key={prefecture.prefectureCode}
              disablePadding
            >
              <ListItemButton role={undefined} onClick={onClickPrefecture(prefecture.prefectureCode)} dense>
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <Checkbox
                    edge="start"
                    checked={selectedPrefectureCodes.indexOf(prefecture.prefectureCode) !== -1}
                    tabIndex={-1}
                    inputProps={{ 'aria-labelledby': labelId }}                  
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={prefecture.prefectureName} />
              </ListItemButton>
            </ListItem>
          )
        }) }
      </List>
    </Stack>
  )
}